import type { StepConfig, PropertyFormData } from "../types";
import {
  propertyTypeOptions,
  hausSubTypeOptions,
  gewerbeSubTypeOptions,
  grundstuckSubTypeOptions,
  qualityOptions,
  conditionOptions,
  usageOptions,
  reasonOptions,
  timelineOptions,
  propertyFeaturesOptions,
} from "./fieldDefinitions";

/**
 * Get sub-property type options based on property type
 */
export function getSubPropertyTypeOptions(propertyType: string) {
  switch (propertyType) {
    case "Haus":
      return hausSubTypeOptions;
    case "Gewerbe":
      return gewerbeSubTypeOptions;
    case "Grundstück":
      return grundstuckSubTypeOptions;
    default:
      return [];
  }
}

/**
 * All form steps configuration - New simplified flow
 * Each step has 1-2 questions and auto-advances on selection
 */
export const formSteps: StepConfig[] = [
  // Step 1: Property Type Selection
  {
    id: "property-type",
    title: "Wählen Sie die Immobilienart",
    subtitle: "Bitte wählen Sie die Art Ihrer Immobilie aus.",
    fields: [
      {
        name: "propertyType",
        type: "radio-card",
        options: propertyTypeOptions,
        autoAdvance: true, // Auto-advance on selection
      },
    ],
  },

  // Step 2: Property Subtype (conditional - skip for Wohnung)
  {
    id: "sub-type",
    title: "Wählen Sie die genaue Immobilienart",
    subtitle: "Bitte wählen Sie die spezifische Art Ihrer Immobilie aus.",
    fields: [
      {
        name: "subPropertyType",
        type: "radio-card",
        options: [], // Dynamically populated based on propertyType
        autoAdvance: true,
      },
    ],
    skip: (formData: PropertyFormData) => formData.propertyType === "Wohnung",
  },

  // Step 3: Property Size
  {
    id: "property-size",
    title: "", // Will be set dynamically based on property type
    subtitle:
      "Sollten sie den exakten Wert nicht kennen, tragen Sie bitte eine Schätzung ein",
    fields: [
      {
        name: "plotSize", // For houses, commercial, land
        type: "suffix",
        // No label - headline describes the input
        suffix: "m²",
        placeholder: "z.B. 500",
        conditional: (formData: PropertyFormData) =>
          formData.propertyType !== "Wohnung",
      },
      {
        name: "livingSpace", // For apartments
        type: "suffix",
        // No label - headline describes the input
        suffix: "m²",
        placeholder: "z.B. 80",
        conditional: (formData: PropertyFormData) =>
          formData.propertyType === "Wohnung",
      },
    ],
  },

  // Step 4: Additional Size Details
  {
    id: "additional-size",
    title: "", // Will be set dynamically based on property type
    subtitle:
      "Sollten sie den exakten Wert nicht kennen, tragen Sie bitte eine Schätzung ein",
    fields: [
      {
        name: "rooms", // For apartments
        type: "increment",
        // No label - headline describes the input
        min: 0,
        conditional: (formData: PropertyFormData) =>
          formData.propertyType === "Wohnung",
      },
      {
        name: "livingSpace", // For houses
        type: "suffix",
        // No label - headline describes the input
        suffix: "m²",
        placeholder: "z.B. 150",
        conditional: (formData: PropertyFormData) =>
          formData.propertyType === "Haus",
      },
      {
        name: "commercialArea", // For commercial
        type: "suffix",
        // No label - headline describes the input
        suffix: "m²",
        placeholder: "z.B. 200",
        conditional: (formData: PropertyFormData) =>
          formData.propertyType === "Gewerbe",
      },
    ],
  },

  // Step 5: Construction Year
  {
    id: "construction-year",
    title: "In welchem Jahr wurde die Immobilie erbaut?",
    subtitle:
      "Sollten sie den exakten Wert nicht kennen, tragen Sie bitte eine Schätzung ein",
    fields: [
      {
        name: "constructionYear",
        type: "number",
        // No label - headline describes the input
        placeholder: "z.B. 1996",
      },
    ],
    skip: (formData: PropertyFormData) =>
      formData.propertyType === "Grundstück",
  },

  // Step 6: Property Features (Checkboxes)
  {
    id: "property-features",
    title: "Welche Ausstattung hat Ihre Immobilie?",
    subtitle: "Bitte markieren Sie alle zutreffenden Felder.",
    fields: [
      {
        name: "propertyFeatures",
        type: "checkbox-group",
        options: propertyFeaturesOptions,
      },
    ],
    skip: (formData: PropertyFormData) =>
      formData.propertyType === "Grundstück" ||
      formData.propertyType === "Gewerbe",
  },

  // Step 7: Quality
  {
    id: "quality",
    title: "Wie ist die Qualität der Ausstattung Ihrer Immobilie?",
    subtitle: "Bad, Küche, Boden, Fenster, etc.",
    fields: [
      {
        name: "quality",
        type: "radio-card",
        options: qualityOptions,
        autoAdvance: true,
      },
    ],
    skip: (formData: PropertyFormData) =>
      formData.propertyType === "Grundstück",
  },

  // Step 8: Condition
  {
    id: "condition",
    title: "Wie ist der Zustand der Immobilie?",
    subtitle: "Bitte bewerten Sie den allgemeinen Zustand der Immobilie.",
    fields: [
      {
        name: "condition",
        type: "radio-card",
        options: conditionOptions,
        autoAdvance: true,
      },
    ],
    skip: (formData: PropertyFormData) =>
      formData.propertyType === "Grundstück",
  },

  // Step 9: Usage
  {
    id: "usage",
    title: "Wie wird die Immobilie aktuell genutzt?",
    subtitle: "Bitte geben Sie an, wie die Immobilie derzeit verwendet wird.",
    fields: [
      {
        name: "usage",
        type: "radio-card",
        options: usageOptions,
        autoAdvance: true,
      },
    ],
    skip: (formData: PropertyFormData) =>
      formData.propertyType === "Grundstück",
  },

  // Step 10: Purpose
  {
    id: "purpose",
    title: "Wofür benötigen Sie die Bewertung?",
    subtitle: "Bitte wählen Sie den Grund für Ihre Bewertungsanfrage aus.",
    fields: [
      {
        name: "reason",
        type: "radio-card",
        options: reasonOptions,
        autoAdvance: true,
      },
    ],
  },

  // Step 11: Property Search (conditional on Kaufabsicht)
  {
    id: "property-search",
    title: "Finden Sie jetzt Ihr neues Zuhause mit Jakobs Immobilien",
    subtitle:
      "Für Kaufinteressenten stellen wir direkt passende Immobilienangebote zur Verfügung.",
    fields: [
      {
        name: "propertySearchAction",
        type: "radio-card",
        options: [
          {
            label: "Jetzt Immobilie finden",
            value: "search",
            icon: "/Icons/Abfrage/abfrage-grund-kaufabsicht.svg",
          },
        ],
        columns: 1,
        onSelectNavigate: "/immobilien",
      },
    ],
    skip: (formData: PropertyFormData) => formData.reason !== "Kaufabsicht",
  },

  // Step 12: Timeline (conditional on purpose)
  {
    id: "timeline",
    title: "Zeitlicher Horizont",
    subtitle:
      "Bitte geben Sie an, wann Sie planen zu verkaufen oder zu kaufen.",
    fields: [
      {
        name: "timeline",
        type: "radio-card",
        options: timelineOptions,
        autoAdvance: true,
      },
    ],
    skip: (formData: PropertyFormData) =>
      formData.reason !== "Verkaufsabsicht" &&
      formData.reason !== "Kaufabsicht",
  },

  // Step 13: Address
  {
    id: "address",
    title: "Wo befindet sich Ihre Immobilie?",
    subtitle:
      "Für ein präzises Ergebnis ist die genau Adresse entscheidend. Wir verwenden Ihre Daten ausschließlich für die Bewertung.",
    fields: [
      {
        name: "city",
        type: "address-search",
      },
    ],
  },

  // Step 14: Contact
  {
    id: "contact",
    title: "Ihre Bewertung ist erstellt!",
    subtitle:
      "Bitte geben Sie Ihre Kontaktdaten ein, um das Ergebnis zu erhalten. Unsere Experte werden sich telefonisch bei Ihnen melden.",
    fields: [], // Contact fields use separate state - use customRender
  },
];

/**
 * Get step configuration by ID
 */
export function getStepConfig(stepId: string): StepConfig | undefined {
  return formSteps.find((step) => step.id === stepId);
}

/**
 * Get dynamic title for step based on property type
 */
export function getStepTitle(
  stepId: string,
  formData: PropertyFormData
): string {
  const step = getStepConfig(stepId);
  if (!step) return "";

  // Dynamic titles based on property type
  if (stepId === "property-size") {
    switch (formData.propertyType) {
      case "Haus":
        return "Wie groß ist Ihr Grundstück?";
      case "Wohnung":
        return "Wie groß ist Ihre Wohnung?";
      case "Gewerbe":
        return "Wie groß ist Ihr Grundstück?";
      case "Grundstück":
        return "Wie groß ist Ihr Grundstück?";
      default:
        return "";
    }
  }

  if (stepId === "additional-size") {
    switch (formData.propertyType) {
      case "Wohnung":
        return "Wie viele Zimmer hat Ihre Wohnung?";
      case "Haus":
        return "Wie groß ist Ihre Wohnfläche?";
      case "Gewerbe":
        return "Wie groß ist die Wohn- und Gewerbefläche?";
      case "Grundstück":
        return ""; // Skip this step for Grundstück
      default:
        return "";
    }
  }

  return step.title || "";
}
